<template>
  <Modal
    :modal-style="{ width: '60vw', maxWidth: '60vw' }"
    title="Archiving User"
  >
    <div class="card q-pa-lg column no-wrap full-height scroll">
      <div>
        Before you can deactivate this user:
        <ul>
          <li v-if="isPrimaryContact">
            You need to select a new <strong>Key Contact</strong>
            for the organisation.
          </li>
          <li v-if="isSecondaryContact">
            You need to select a new <strong>Secondary Key Contact</strong>
            for the organisation.
          </li>
          <li>
            The user has multiple associations that will be deactivated.
            Alternatively, you can move each section to another user.
          </li>
        </ul>
      </div>
      <div class="q-ml-lg q-mb-lg">
        <div class="row">
          <span class="q-ma-sm full-width"
            >Move all associations to the selected user</span
          >
          <AllUserDropdown
            v-model="selectedUser"
            class="q-mb-sm col-5"
            clearable
            dense
            :emit-value="false"
            label="New User"
            :organisation-id="user.organisationId"
            @update:model-value="setUserForAllAssociations"
          />
        </div>
        <div class="q-mt-md">
          <span>Or continue to manage each section manually</span>
        </div>
      </div>
      <QSeparator />
      <QForm
        class="q-mt-md"
        @submit.prevent="save"
      >
        <div class="row q-mt-lg">
          <div class="col-6 q-gutter-md">
            <div
              v-if="isPrimaryContact"
              class="q-pl-md"
            >
              <strong>Organisation Key Contact</strong>
              <AllUserDropdown
                v-model="primaryContact"
                class="col-10 q-mb-lg q-mt-sm"
                clearable
                dense
                :emit-value="false"
                label="Organisation's Key Contact"
                :organisation-id="user.organisationId"
                :rules="[(val) => !!val || 'Must select an user']"
              />
            </div>
            <div
              v-if="isSecondaryContact"
              class="q-pl-md"
            >
              <strong>Organisation Secondary Key Contact</strong>
              <AllUserDropdown
                v-model="secondaryContact"
                class="col-5 q-mb-lg q-mt-sm"
                clearable
                dense
                :emit-value="false"
                label="Organisation's Secondary Key Contact"
                :organisation-id="user.organisationId"
                :rules="[(val) => !!val || 'Must select an user']"
              />
            </div>
          </div>
          <UserAssociationsTable
            v-if="streamGroups.length"
            v-model:data="streamGroups"
            :organisation-id="user.organisationId"
            type="MentionStreamGroups"
          />
          <UserAssociationsTable
            v-if="scheduledReportsSpecs.length"
            v-model:data="scheduledReportsSpecs"
            :organisation-id="user.organisationId"
            type="ScheduledReportSpecs"
          />
          <UserAssociationsTable
            v-if="socialGroups.length"
            v-model:data="socialGroups"
            :organisation-id="user.organisationId"
            type="SocialStreamGroups"
            @set-all="updateUserForSection('socialGroups', null)"
          />
          <UserAssociationsTable
            v-if="organisationReportSpecs.length"
            v-model:data="organisationReportSpecs"
            :organisation-id="user.organisationId"
            type="OrganisationReportSpecs"
          />
        </div>
        <div class="row q-mt-md justify-end">
          <BaseButton
            label="Cancel"
            @click="$emit('close')"
          />
          <BaseButton
            color="primary"
            label="Confirm changes"
            type="submit"
          />
        </div>
      </QForm>
    </div>
  </Modal>
</template>

<script>
import { groupBy } from "lodash-es";
import { storeToRefs } from "pinia";

import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { streamTypes } from "shared/constants";
import { labelForStreamType } from "shared/helpers/stream";
import {
  OrganisationReportSpec,
  ScheduledReportSpecification,
  Stream,
} from "shared/resources";

import AllUserDropdown from "src/components/common/AllUserDropdown.vue";
import UserAssociationsTable from "src/components/streams/UserAssociationsTable.vue";
import { useUserStore } from "src/stores/user";

export default {
  name: "DeactivateUserModal",
  components: {
    AllUserDropdown,
    BaseButton,
    UserAssociationsTable,
    Modal,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "confirm"],
  setup() {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return { organisation };
  },
  data() {
    return {
      selectedUser: null,
      streamGroups: [],
      streams: [],
      socialGroups: [],
      scheduledReportsSpecs: [],
      organisationReportSpecs: [],
      primaryContact: null,
      secondaryContact: null,
    };
  },
  computed: {
    streamsByGroup() {
      return groupBy(this.streams, "groupId");
    },
    isPrimaryContact() {
      return Number(this.organisation.keyContactId) === Number(this.user.id);
    },
    isSecondaryContact() {
      return (
        Number(this.organisation.secondaryKeyContactId) === Number(this.user.id)
      );
    },
  },
  mounted() {
    this.loadUserData();
  },
  methods: {
    formatParams(list) {
      return list
        .filter((item) => item.newUser)
        .map((item) => ({
          id: item.id,
          new_user_id: item.newUser.id,
        }));
    },
    async loadUserData() {
      try {
        this.$q.loading.show();

        const promises = [
          this.loadStreams(),
          this.loadStreamGroups(),
          this.loadScheduledReports(),
          this.loadOrganisationReportSpecs(),
        ];

        const responses = await Promise.all(promises);

        this.streams = responses[0].data;
        this.streamGroups = [];

        responses[1].data.forEach((group) => {
          if (group.stream_type === streamTypes.organisationBrief) {
            return;
          }

          const newGroup = {
            ...group,
            id: group.id,
            label: group.label,
            type: labelForStreamType(group.stream_type),
            newUser: null,
            expanded: false,
            streams: this.streamsByGroup[group.id],
          };

          if (group.stream_type === streamTypes.mentionStream) {
            this.streamGroups.push(newGroup);
          } else {
            this.socialGroups.push(newGroup);
          }
        });

        this.scheduledReportsSpecs = responses[2].data.map((spec) => {
          const streams = [];

          spec.sections.forEach((section) => {
            streams.concat(
              section.rules.forEach((rule) => streams.push(rule.stream))
            );
          });

          const newGroup = {
            label: spec.subject,
            id: spec.id,
            type: "Scheduled Report",
            schedules: spec.schedules.length,
            usersWithAccess: spec.userAccessibles.length,
            newUser: null,
            expanded: false,
            streams,
          };

          return newGroup;
        });

        this.organisationReportSpecs = responses[3].data.map((spec) => ({
          label: spec.title,
          id: spec.id,
          newUser: null,
        }));
      } catch (error) {
        this.$error("Unable to load user data", error);
      } finally {
        this.$q.loading.hide();
      }
    },
    loadStreams() {
      return Stream.where({
        userId: this.user.id,
        organisationId: null,
        type: [streamTypes.mentionStream, streamTypes.socialStream],
        archived: false,
        expired: false,
      })
        .includes(["streamNotifications", { streamsUsers: "user" }])
        .per(1000)
        .all();
    },
    loadStreamGroups() {
      return this.$streemApiAdmin.get("stream_groups", {
        params: { user_id: this.user.id },
      });
    },
    loadScheduledReports() {
      return ScheduledReportSpecification.includes([
        "schedules",
        "userAccessibles",
        { sections: [{ rules: ["stream"] }] },
      ])
        .where({ userId: this.user.id, archived: false })
        .per(1000)
        .all();
    },
    loadOrganisationReportSpecs() {
      return OrganisationReportSpec.where({ userAccountId: this.user.id })
        .per(1000)
        .all();
    },
    async save() {
      try {
        this.$q.loading.show();

        const params = {
          stream_groups: this.formatParams(this.streamGroups),
          social_groups: this.formatParams(this.socialGroups),
          scheduled_reports_specs: this.formatParams(
            this.scheduledReportsSpecs
          ),
          organisation_report_specs: this.formatParams(
            this.organisationReportSpecs
          ),
          ...(this.isPrimaryContact
            ? { key_contact_id: this.primaryContact?.id }
            : {}),
          ...(this.isSecondaryContact
            ? { secondary_key_contact_id: this.secondaryContact?.id }
            : {}),
        };

        await this.$streemApiAdmin.put(`users/${this.user.id}/deactivate`, {
          params,
        });

        this.$emit("confirm");
      } catch (error) {
        this.$error("Unable to deactivate user", error);
      } finally {
        this.$q.loading.hide();
      }
    },
    updateUserForSection(section, user) {
      this[section].forEach((item) => Object.assign(item, { newUser: user }));
    },
    setUserForAllAssociations() {
      this.primaryContact = this.selectedUser;
      this.secondaryContact = this.selectedUser;
      this.updateUserForSection("streamGroups", this.selectedUser);
      this.updateUserForSection("socialGroups", this.selectedUser);
      this.updateUserForSection("scheduledReportsSpecs", this.selectedUser);
      this.updateUserForSection("organisationReportSpecs", this.selectedUser);
    },
  },
};
</script>
